import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { db, auth } from '../../firebaseConfig';
import { collection, addDoc, doc, setDoc, deleteDoc, onSnapshot, getDocs, query, getDoc } from 'firebase/firestore';
import '../../styles/CallComponent.css';
import { FaPhone } from "react-icons/fa";
import { FaPhoneSlash } from "react-icons/fa";
import { FaPhoneVolume } from "react-icons/fa6";

const CallComponent = () => {
  const { chatId } = useParams();
  const [searchParams] = useSearchParams();
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const pcRef = useRef(null);
  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const [localStream, setLocalStream] = useState(null);
  const [callStarted, setCallStarted] = useState(false);
  const [isCaller, setIsCaller] = useState(false);
  const [iceCandidates, setIceCandidates] = useState([]);
  const [incomingOffer, setIncomingOffer] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState({ name: '', photoURL: '' });
  const [isSelfVerified, setIsSelfVerified] = useState(false);
  const [verificationChecked, setVerificationChecked] = useState(false);
  const [isOtherVerified, setIsOtherVerified] = useState(false);
const [isSelfPaid, setIsSelfPaid] = useState(false);
const [isOtherPaid, setIsOtherPaid] = useState(false);

  const isAnswered = searchParams.get('answered') === 'true';

  const checkAgeVerification = async (uid) => {
    const ageVerificationDoc = await getDoc(doc(db, 'age_verification_checked', uid));
    return ageVerificationDoc.exists();
  };


  const checkPaidStatus = async (uid) => {
    const payDoc = await getDoc(doc(db, 'pay_checked', uid));
    if (!payDoc.exists()) {
      console.warn(`pay_checked for ${uid} does not exist`);
      return false;
    }
    const data = payDoc.data();
    const expiration = data.expiration?.toDate?.();
    console.log(`Pay status for ${uid}:`, data, '→ expiration:', expiration);
  
    return expiration && expiration > new Date();
  };
  
  
  const initWebRTC = async () => {
    try {
      const servers = {
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      };

      pcRef.current = new RTCPeerConnection(servers);

      pcRef.current.onicecandidate = (event) => {
        if (event.candidate) {
          const candidate = event.candidate.toJSON();
          addDoc(collection(db, `chats/${chatId}/iceCandidates`), candidate);
        }
      };

      pcRef.current.ontrack = (event) => {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.streams[0];
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => {
        pcRef.current.addTrack(track, stream);
      });
      localAudioRef.current.srcObject = stream;
      setLocalStream(stream);

    } catch (error) {
      console.error('Error initializing WebRTC:', error);
    }
  };


  

  const handleAnswer = async (offer) => {
    try {
      // 💡 まず PeerConnection を強制的に初期化（前の状態が不正なまま残ってるときのため）
      if (pcRef.current) {
        console.log("リセットのため、既存PeerConnectionをクローズします");
        pcRef.current.close();
        pcRef.current = null;
      }
      await initWebRTC();
  
      // その後、offer を設定
      await pcRef.current.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pcRef.current.createAnswer();
      await pcRef.current.setLocalDescription(answer);
      await setDoc(doc(db, `chats/${chatId}/answers/remoteAnswer`), {
        ...answer,
        uid: user.uid,
      });
      setCallStarted(true);
  
      for (const candidate of iceCandidates) {
        try {
          await pcRef.current.addIceCandidate(candidate);
        } catch (e) {
          console.error("Error adding ICE candidate:", e);
        }
      }
      setIceCandidates([]);
    } catch (error) {
      console.error("Error handling answer:", error);
    }
  };
  

  const resetCall = async () => {
    if (pcRef.current) {
      await deleteDoc(doc(db, `chats/${chatId}/offers/remoteOffer`));
      await deleteDoc(doc(db, `chats/${chatId}/answers/remoteAnswer`));
      pcRef.current.close();
      pcRef.current = null;
    }

    if (localStream) {
      localStream.getTracks().forEach(track => {
        track.stop();
      });
      setLocalStream(null);
      console.log('Local audio tracks stopped and stream removed');
    }

    if (remoteAudioRef.current && remoteAudioRef.current.srcObject) {
      remoteAudioRef.current.srcObject.getTracks().forEach(track => {
        track.stop();
      });
      remoteAudioRef.current.srcObject = null;
      console.log('Remote audio tracks stopped and stream removed');
    }

    setCallStarted(false);
    setIsCaller(false);
    setIncomingOffer(null);

    try {
      if (!isAnswered) {
        await deleteDoc(doc(db, `chats/${chatId}/offers/remoteOffer`));
        await deleteDoc(doc(db, `chats/${chatId}/answers/remoteAnswer`));
        console.log('Firestore offers and answers deleted');
      }

      const iceCandidatesQuery = query(collection(db, `chats/${chatId}/iceCandidates`));
      const querySnapshot = await getDocs(iceCandidatesQuery);
      querySnapshot.forEach(async (doc) => {
        await deleteDoc(doc.ref);
      });
      console.log('Firestore ICE candidates deleted');
    } catch (error) {
      console.error('Error deleting Firestore documents:', error);
    }

    console.log('Call reset and mic turned off');

    await initWebRTC();
  };

  useEffect(() => {
    if (!chatId || !user) return;
  
    const checkStatuses = async () => {
      try {
        const chatDoc = await getDoc(doc(db, 'chats', chatId));
        if (!chatDoc.exists()) return;
    
        const participants = chatDoc.data().participants;
        if (!participants.includes(user.uid)) {
          console.warn('User is not a participant in this chat.');
          return;
        }
    
        // ✅ ここで checkAgeVerification や checkPaidStatus が呼ばれていない（無意味になってる）
      } catch (error) {
        console.error('Permission or network error:', error.message);
      }
    };
    
  
    checkStatuses();
  }, [chatId, user]);
  
  


  const fetchRecipientInfo = async () => {
    try {
      const chatDoc = await getDoc(doc(db, 'chats', chatId));
      if (chatDoc.exists()) {
        const participants = chatDoc.data().participants;
        const recipientUid = participants.find((participant) => participant !== user.uid);
        if (recipientUid) {
          const recipientDoc = await getDoc(doc(db, 'members', recipientUid));
          if (recipientDoc.exists()) {
            setRecipientInfo(recipientDoc.data());
          }
        }
      }
    } catch (error) {
      console.error('Error fetching recipient info:', error);
    }
  };

  useEffect(() => {
    if (!chatId || !user) {
      console.error('chatId or user is missing');
      return;
    }

    const checkVerificationAndFetchInfo = async () => {
      const chatDoc = await getDoc(doc(db, 'chats', chatId));
      if (!chatDoc.exists()) return;
    
      const participants = chatDoc.data().participants;
      const otherUid = participants.find(uid => uid !== user.uid);
      if (!otherUid) return;
    
      // 自分
      const selfVerified = await checkAgeVerification(user.uid);
      const selfPaid = await checkPaidStatus(user.uid);
      setIsSelfVerified(selfVerified);
      setIsSelfPaid(selfPaid);
    
      // 相手 ← これが足りてない！
      console.log(otherUid)
      const otherVerified = await checkAgeVerification(otherUid);
      const otherPaid = await checkPaidStatus(otherUid);
      setIsOtherVerified(otherVerified);
      setIsOtherPaid(otherPaid);
    
      setVerificationChecked(true);
    
      if (selfVerified && selfPaid) {
        fetchRecipientInfo();
        initWebRTC();
      }
    };
    
    

    checkVerificationAndFetchInfo();

    const unsubscribeOffer = onSnapshot(doc(db, `chats/${chatId}/offers/remoteOffer`), async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const offer = docSnapshot.data();
        if (offer.uid !== user.uid) {
          console.log('Received offer:', offer);
          setIncomingOffer(offer);
        }
      } else {
        console.log('Offer document deleted, allowing startCall again');
        setCallStarted(false);
      }
    });

    const unsubscribeAnswer = onSnapshot(doc(db, `chats/${chatId}/answers/remoteAnswer`), async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const answer = docSnapshot.data();
        if (answer.uid !== user.uid) {
          console.log('Received answer:', answer);
          try {
            await pcRef.current.setRemoteDescription(new RTCSessionDescription(answer));
            setCallStarted(true);

            iceCandidates.forEach(async (candidate) => {
              try {
                await pcRef.current.addIceCandidate(candidate);
              } catch (e) {
                console.error('Error adding buffered ICE candidate:', e);
              }
            });
            setIceCandidates([]);
          } catch (error) {
            console.error('Error handling answer:', error);
          }
        }
      }
    });

    const unsubscribeIceCandidates = onSnapshot(collection(db, `chats/${chatId}/iceCandidates`), (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const candidate = new RTCIceCandidate(change.doc.data());
          if (pcRef.current && pcRef.current.remoteDescription) {
            try {
              await pcRef.current.addIceCandidate(candidate);
            } catch (e) {
              console.error('Error adding received ICE candidate:', e);
            }
          } else {
            setIceCandidates((prev) => [...prev, candidate]);
          }
        }
      });
    });

    const handleBeforeUnload = async (event) => {
      event.preventDefault();
      await resetCall();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      resetCall();
      unsubscribeOffer();
      unsubscribeAnswer();
      unsubscribeIceCandidates();
    };
  }, [chatId, user]);

  useEffect(() => {
    const autoAnswer = async () => {
      if (!isAnswered || !user || callStarted || !pcRef.current) return;
  
      try {
        const offerDoc = await getDoc(doc(db, `chats/${chatId}/offers/remoteOffer`));
        if (offerDoc.exists()) {
          const offerData = offerDoc.data();
          if (offerData.uid !== user.uid) {
            console.log("autoAnswer: Firestoreからofferを取得して応答します");
            await handleAnswer(offerData);
          }
        }
      } catch (e) {
        console.error("自動応答時のエラー:", e);
      }
    };
  
    autoAnswer();
  }, [isAnswered, chatId, user, callStarted]);
  
  

  const startCall = async () => {
    if (!pcRef.current) return;

    console.log('Start Call - Self Verified:', isSelfVerified); // デバッグ情報

    if (!isSelfVerified) {
      console.error('User has not completed age verification');
      return;
    }

    setIsCaller(true);

    const offer = await pcRef.current.createOffer();
    await pcRef.current.setLocalDescription(offer);
    await setDoc(doc(db, `chats/${chatId}/offers/remoteOffer`), { ...offer, uid: user.uid });

    console.log('Offer sent:', offer);
    setCallStarted(true);
  };

  const answerCall = async () => {
    if (!isSelfVerified) {
      console.error('User has not completed age verification');
      return;
    }
  
    if (!pcRef.current) {
      console.log('PeerConnectionが未初期化のため、WebRTCを初期化します');
      await initWebRTC();
    }
  
    if (incomingOffer) {
      await handleAnswer(incomingOffer);
      setIncomingOffer(null);
    }
  };
  

  if (!verificationChecked) {
    return <div>Loading...</div>;
  }

  if (!isSelfVerified) {
    return <div>User has not completed age verification.</div>;
  }
  if (!verificationChecked) {
    return <div>Loading...</div>;
  }
  
  if (!verificationChecked) {
    return <div>Loading...</div>;
  }
  
  // 誰が未確認 or 非課金かを個別にチェック
  const selfNG = !isSelfVerified || !isSelfPaid;
  console.log(isSelfVerified);
  console.log(isSelfPaid);
  const otherNG = !isOtherVerified || !isOtherPaid;
  console.log(isOtherVerified);
  console.log(isOtherPaid);
  
  if (selfNG || otherNG) {
    const who = (selfNG && otherNG)
    ? '（自分・お相手）'
    : selfNG
    ? '（自分）'
    : '（お相手）';
  
    return (
      <div className="call-warning">
        <p> {who}が年齢確認または有料会員でないため通話できません</p>
      </div>
    );
  }
  
  
  return (
    <div className="call-page">
      <div className="recipient-info">
        {/* photoURL が空の場合にはデフォルトの画像を表示 */}
        <img 
          src={recipientInfo.photoURL || '/images/default.png'} 
          alt={recipientInfo.name} 
          className="recipient-avatar" 
        />
        <span className="call-oponent">{recipientInfo.name}さん</span>
      </div>
      {incomingOffer && <h3 className="call-alert">{recipientInfo.name}さんからの着信です</h3>}

        <p className="call-discription">※通話するには、相手がアプリを開いている必要があります</p>
      <audio ref={localAudioRef} autoPlay muted />
      <audio ref={remoteAudioRef} autoPlay />
      <div className="audio-controls">
  <button onClick={startCall} disabled={callStarted}>
    <FaPhone /><br />電話をかける
  </button>

  {(callStarted || incomingOffer) && (
    <button onClick={resetCall} className="reset-button">
      <FaPhoneSlash /><br />通話を切る
    </button>
  )}

  {incomingOffer && (
    <button onClick={answerCall} className="answer-button">
      <FaPhoneVolume /><br />通話を受ける
    </button>
  )}
</div>

    </div>
  );
  
};

export default CallComponent;
